<div class="finance-list-page" *ngIf="canViewReceivables">
    <app-page-shell>
        <p-toast position="top-center"></p-toast>
        <p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

        <app-page-hero [eyebrow]="t('pages.receivables.hero.eyebrow')" [title]="t('pages.receivables.hero.title')"
            [subtitle]="t('pages.receivables.hero.subtitle')">
            <div actions>
                <p-button *ngIf="canGenerateInvoice && !isConsolidatedReadOnly" [label]="generateInvoiceLabel" icon="pi pi-file"
                    (click)="generateInvoice()" [disabled]="!canGenerateSelectedInvoice"
                    [pTooltip]="invoiceSelectionBlocker || (checkingInvoiceSelection ? 'Checking sales order readiness' : '')"></p-button>
                <p-button *ngIf="canGenerateInvoice && !isConsolidatedReadOnly" [label]="t('pages.receivables.actions.manualLine')" icon="pi pi-plus"
                    severity="secondary" [outlined]="true" (click)="createManualReceivable()"></p-button>
                <p-button *ngIf="canExportReceivables" [label]="t('pages.receivables.actions.export')" icon="pi pi-file-excel"
                    severity="secondary" [outlined]="true" (click)="exportToExcel()"></p-button>
            </div>
        </app-page-hero>

        <app-page-data-panel *ngIf="createdInvoiceDrafts.length > 1" title="Created invoice drafts" subtitle="Drafts are grouped by customer and currency. Open a draft to continue.">
            <div class="bulk-actions">
                <p-button *ngFor="let invoice of createdInvoiceDrafts" [label]="invoice.invoiceNumber || 'Open invoice draft'" icon="pi pi-file" severity="secondary" [outlined]="true" (click)="openCreatedInvoice(invoice)"></p-button>
            </div>
        </app-page-data-panel>

        <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

        <app-page-search-panel [title]="t('pages.receivables.search.title')" [activeFilters]="activeFilterCount"
            [resultText]="resultText" [collapsed]="true" (search)="applyFilters()" (clear)="clearFilters()">
            <div class="bulk-actions" style="margin-bottom: .75rem;">
                <p-button *ngFor="let view of savedFilterViews" [label]="view.label" size="small" severity="secondary"
                    [outlined]="true" (click)="applySavedFilter(view)"></p-button>
            </div>
            <div class="finance-search-grid">
                <app-company-scope-selector (scopeChange)="onScopeChange($event)"></app-company-scope-selector>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="client" [options]="clients" [(ngModel)]="selectedClients"
                                optionLabel="name" display="chip" [showClear]="true" styleClass="w-full"
                                (onChange)="onSelectedClientsChange()"></p-multiSelect>
                            <label for="client">{{ t('pages.receivables.search.crmClients') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-address-book"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="contact" [options]="contacts" [(ngModel)]="selectedContacts"
                                optionLabel="displayName" display="chip" [showClear]="true" styleClass="w-full"
                                [disabled]="!selectedClients.length"></p-multiSelect>
                            <label for="contact">{{ t('pages.receivables.search.crmContacts') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-datePicker inputId="dateRange" [(ngModel)]="dateRange" selectionMode="range"
                                [readonlyInput]="true" dateFormat="yy-mm-dd" appendTo="body"
                                styleClass="w-full"></p-datePicker>
                            <label for="dateRange">{{ t('pages.receivables.search.invoicingDateRange') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-tag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="status" [options]="statusOptions" [(ngModel)]="selectedStatuses" optionLabel="label" optionValue="value"
                                display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                            <label for="status">{{ t('pages.receivables.search.status') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-pencil"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <p-multiSelect inputId="sourceType" [options]="sourceTypeOptions" [(ngModel)]="selectedSourceTypes" optionLabel="label" optionValue="value"
                                display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                            <label for="sourceType">{{ t('pages.receivables.search.sourceType') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="reference" type="text" pInputText [(ngModel)]="referenceFilter" />
                            <label for="reference">{{ t('pages.receivables.search.referenceNumber') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-link"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="sourceReference" type="text" pInputText [(ngModel)]="sourceReferenceFilter" />
                            <label for="sourceReference">{{ t('pages.receivables.search.sourceReference') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>

                <div class="field">
                    <p-inputGroup>
                        <p-inputGroupAddon><i class="pi pi-file"></i></p-inputGroupAddon>
                        <p-iftalabel>
                            <input id="invoiceNumber" type="text" pInputText [(ngModel)]="invoiceNumberFilter" />
                            <label for="invoiceNumber">{{ t('pages.receivables.search.invoiceNumber') }}</label>
                        </p-iftalabel>
                    </p-inputGroup>
                </div>
            </div>
        </app-page-search-panel>

        <app-page-data-panel [title]="t('pages.receivables.dataPanel.title')" [subtitle]="t('pages.receivables.dataPanel.subtitle')"
            [resultText]="resultText">
            <div actions>
                <p-button *ngIf="canExportReceivables" [label]="t('pages.receivables.actions.export')" icon="pi pi-file-excel"
                    severity="secondary" [outlined]="true" (click)="exportToExcel()"></p-button>
            </div>

            <div class="bulk-action-bar" *ngIf="selectedReceivables.length && !isConsolidatedReadOnly">
                <div>
                    <strong>{{ selectedReceivables.length }}</strong> {{ t('pages.receivables.bulk.selected') }},
                    <strong>{{ formatMoney(getSelectedTotal()) }}</strong>
                    <small *ngIf="invoiceSelectionBlocker" class="block text-red-600 mt-1" role="alert">{{ invoiceSelectionBlocker }}</small>
                </div>
                <div class="bulk-actions">
                    <p-button *ngIf="canGenerateInvoice" [label]="generateInvoiceLabel" icon="pi pi-file"
                        (click)="generateInvoice()" [disabled]="!canGenerateSelectedInvoice"
                        [pTooltip]="invoiceSelectionBlocker || (checkingInvoiceSelection ? 'Checking sales order readiness' : '')"></p-button>
                    <p-button *ngIf="canEditReceivables" [label]="t('pages.receivables.actions.hold')" icon="pi pi-pause"
                        severity="warn" [outlined]="true" (click)="holdSelected()"></p-button>
                    <p-button *ngIf="canEditReceivables" [label]="t('pages.receivables.actions.release')" icon="pi pi-play"
                        severity="success" [outlined]="true" (click)="releaseSelected()"></p-button>
                    <p-button *ngIf="canEditReceivables" [label]="t('pages.receivables.actions.dispute')" icon="pi pi-exclamation-triangle"
                        severity="danger" [outlined]="true" (click)="disputeSelected()"></p-button>
                    <p-button *ngIf="canEditReceivables" [label]="t('pages.receivables.actions.submitApproval')" icon="pi pi-send"
                        severity="secondary" [outlined]="true" (click)="submitSelectedForApproval()"></p-button>
                    <p-button *ngIf="canGenerateInvoice" [label]="t('pages.receivables.actions.manualLine')" icon="pi pi-plus"
                        severity="secondary" [outlined]="true" (click)="createManualReceivable()"></p-button>
                </div>
            </div>

            <app-accounting-directory-controls documentType="RECEIVABLE" [filters]="directoryFilters"
                [searchText]="searchText" [selectedStatuses]="selectedStatuses" [pendingStatuses]="pendingStatuses" (searchTextChange)="onDirectorySearch($event)"
                (modeChange)="onDirectoryModeChange($event)" (pending)="showPendingStatuses()"
                (all)="showAllStatuses()"></app-accounting-directory-controls>
            <p-table *ngIf="directoryMode === 'LIST'" class="finance-table" [value]="allReceivables" dataKey="receivableId" [paginator]="true"
                [rows]="rows" [first]="first" [showCurrentPageReport]="true" [totalRecords]="totalRecords"
                [lazy]="true" [currentPageReportTemplate]="currentPageReportTemplate"
                [lazyLoadOnInit]="false" (onLazyLoad)="loadData($event)" [rowsPerPageOptions]="[20, 50, 100]"
                [(selection)]="selectedReceivables" (selectionChange)="onReceivableSelectionChange($event)" [tableStyle]="{ 'min-width': '0' }">
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width: 3.5rem"><p-tableHeaderCheckbox *ngIf="!isConsolidatedReadOnly"></p-tableHeaderCheckbox></th>
                        <th>{{ t('pages.receivables.table.sourceReference') }}</th>
                        <th>{{ t('pages.receivables.table.customer') }}</th>
                        <th>{{ t('pages.receivables.table.billingAmount') }}</th>
                        <th>{{ t('pages.receivables.table.readiness') }}</th>
                        <th>{{ t('pages.receivables.table.datesOwner') }}</th>
                        <th>{{ t('pages.receivables.table.actions') }}</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-receivable>
                    <tr>
                        <td [attr.data-label]="t('pages.receivables.table.select')"><p-tableCheckbox *ngIf="!isConsolidatedReadOnly" [value]="receivable"></p-tableCheckbox></td>
                        <td [attr.data-label]="t('pages.receivables.table.sourceReference')">
                            <div class="stack-cell">
                                <button type="button" class="cell-title link-button" (click)="viewDetails(receivable)">
                                    {{ displayDocumentReference(receivable.receivableReferenceNumber) }}
                                </button>
                                <div class="cell-meta">
                                    <span class="code-pill">{{ receivable.receivableTxnType || t('pages.receivables.table.source') }}</span>
                                    <span *ngIf="receivable.invoiceNumber">{{ t('pages.receivables.table.invoice') }} {{ receivable.invoiceNumber }}</span>
                                </div>
                                <ng-container *ngIf="headerSourceReferences(receivable) as headerRefs">
                                    <div class="cell-meta" *ngIf="headerRefs.length">
                                        {{ t('pages.receivables.table.headerSource') }}: {{ headerRefs.join(', ') }}
                                    </div>
                                </ng-container>
                                <ng-container *ngIf="lineSourceReferences(receivable) as lineRefs">
                                    <div class="cell-meta" *ngIf="lineRefs.length">
                                        {{ t('pages.receivables.table.lineSources') }}: {{ lineRefs.slice(0, 2).join(', ') }}
                                        <span *ngIf="lineRefs.length > 2" tabindex="0" [pTooltip]="lineRefs.join(', ')"
                                            [attr.aria-label]="t('pages.receivables.table.lineSources') + ': ' + lineRefs.join(', ')">+{{ lineRefs.length - 2 }}</span>
                                    </div>
                                </ng-container>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.receivables.table.customer')">
                            <div class="stack-cell">
                                <span class="cell-title">{{ crmPartyContext.displayName(receivable) }}</span>
                                <div class="cell-meta">{{ t('pages.receivables.table.contact') }}: {{ crmPartyContext.contactDisplayName(receivable) }}</div>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.receivables.table.billingAmount')">
                            <div class="amount-block">
                                <span class="amount-primary">{{ receivable.totalAmount | currency:receivable.country?.currencyCode:(receivable.country?.currencyCode || 'SAR') + ' ' }}</span>
                                <span class="amount-secondary">{{ t('accountingAmounts.beforeTax') }} {{ receivable.amountExclTax != null ? (receivable.amountExclTax | currency:receivable.country?.currencyCode:(receivable.country?.currencyCode || 'SAR') + ' ') : '—' }}</span>
                                <span class="amount-secondary">{{ t('pages.receivables.table.tax') }} {{ receivable.taxAmount | currency:receivable.country?.currencyCode:(receivable.country?.currencyCode || 'SAR') + ' ' }}</span>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.receivables.table.readiness')">
                            <div class="status-stack">
                                <p-tag [severity]="statusSeverity(receivable.status)" [value]="statusLabel(receivable.status)"></p-tag>
                                <span class="muted-pill">{{ t('pages.receivables.table.lineCount', { count: receivable.accountReceivablesLines?.length || 0 }) }}</span>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.receivables.table.datesOwner')">
                            <div class="stack-cell">
                                <span class="cell-title">{{ receivable.invoicingDate | date:'yyyy-MM-dd' }}</span>
                                <div class="cell-meta vertical">
                                    <span>{{ t('pages.receivables.table.created') }} {{ receivable.createdAt | date:'yyyy-MM-dd' }}</span>
                                    <span>{{ t('pages.receivables.table.expected') }} {{ receivable.expectedReceivingDate | date:'yyyy-MM-dd' }}</span>
                                </div>
                            </div>
                        </td>
                        <td [attr.data-label]="t('pages.receivables.table.actions')">
                            <div class="row-actions">
                                <p-button icon="pi pi-eye" [rounded]="true" [text]="true" pTooltip="View receivable"
                                    (click)="viewReceivable(receivable)"></p-button>
                                <p-button *ngIf="canEditReceivable(receivable)" icon="pi pi-pencil" [rounded]="true" [text]="true"
                                    severity="secondary" [pTooltip]="t('pages.receivables.tooltips.editManualLine', 'Edit manual/staged line')" (click)="editReceivable(receivable)"></p-button>
                                <p-button icon="pi pi-external-link" [rounded]="true" [text]="true" [pTooltip]="t('pages.receivables.tooltips.viewSource', 'View source')"
                                    (click)="viewDetails(receivable)"></p-button>
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7">
                            <div class="empty-state">
                                <i class="pi pi-inbox"></i>
                                <strong>{{ t('pages.receivables.empty.title') }}</strong>
                                <span>{{ t('pages.receivables.empty.subtitle') }}</span>
                            </div>
                        </td>
                    </tr>
                </ng-template>
            </p-table>
        </app-page-data-panel>
    </app-page-shell>

    <p-drawer [(visible)]="isPreviewVisible" position="right" styleClass="finance-preview-drawer">
        <ng-template pTemplate="header">
            <div class="stack-cell">
                <strong>{{ displayDocumentReference(previewReceivable?.receivableReferenceNumber) }}</strong>
                <span class="cell-meta">{{ t('pages.receivables.preview.lineCount', { count: previewReceivable?.accountReceivablesLines?.length || 0 }) }}</span>
            </div>
        </ng-template>
        <div class="preview-list" *ngIf="previewReceivable?.accountReceivablesLines?.length; else noReceivableLines">
            <div class="preview-line" *ngFor="let line of previewReceivable?.accountReceivablesLines">
                <div class="preview-line-header">
                    <div>
                        <div class="preview-line-title">{{ line.lineDescription || t('pages.receivables.preview.lineItem') }}</div>
                        <div class="cell-meta">{{ line.receivablesLineType || 'MISCELLANEOUS' }} · {{ line.receivableLineRefNumber || 'No source reference' }}</div>
                        <div class="cell-meta" *ngIf="line.itemDescriptionLocal" dir="rtl">{{ line.itemDescriptionLocal }}</div>
                        <div class="cell-meta" *ngIf="line.invoiceComment">{{ line.invoiceComment }}</div>
                    </div>
                    <div class="preview-line-amount">{{ formatMoney(line.amount, previewReceivable?.country?.currencyCode) }}</div>
                </div>
            </div>
        </div>
        <ng-template #noReceivableLines>
            <div class="preview-empty">{{ t('pages.receivables.preview.noLines') }}</div>
        </ng-template>
    </p-drawer>
</div>

<div class="finance-list-page" *ngIf="!canViewReceivables">
    <p-toast position="top-center"></p-toast>
    <div class="empty-state">
        <i class="pi pi-lock"></i>
        <strong>{{ t('pages.receivables.noAccess.title') }}</strong>
        <span>{{ t('pages.receivables.noAccess.subtitle') }}</span>
    </div>
</div>
